import { z } from "zod";
import {
  normalizeHireTeamHandoffCtaClusters,
  type HireTeamHandoffCtaCluster,
} from "~/lib/marketplace/hire-team-handoff-ctas";

export type { HireTeamHandoffCtaCluster };

export type HireAgentResponsibilities = {
  liveChat: boolean;
  teamHandoff: boolean;
  textMessages: boolean;
  email: boolean;
  browserCalls: boolean;
  phoneCalls: boolean;
  videoCalls: boolean;
  welcomeVideo: boolean;
  messenger: boolean;
  whatsapp: boolean;
  appointmentsScheduling: boolean;
};

export const hireAgentResponsibilitiesSchema = z.object({
  liveChat: z.boolean(),
  teamHandoff: z.boolean(),
  textMessages: z.boolean(),
  email: z.boolean(),
  browserCalls: z.boolean(),
  phoneCalls: z.boolean(),
  videoCalls: z.boolean(),
  welcomeVideo: z.boolean(),
  messenger: z.boolean(),
  whatsapp: z.boolean(),
  appointmentsScheduling: z.boolean(),
});

export const hireContactSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  email: z.string().email(),
  phone: z.string().max(40).optional(),
});

export type HireContact = z.infer<typeof hireContactSchema>;

const hireScrapedLocationSchema = z.object({
  name: z.string().min(1).max(200),
  address: z.string().max(500).optional(),
  city: z.string().max(120).optional(),
  state: z.string().max(20).optional(),
  postalCode: z.string().max(20).optional(),
  phone: z.string().max(40).optional(),
  businessHours: z.string().max(500).optional(),
});

export const hireAgentPreferencesSchema = z.object({
  agentName: z.string().min(1).max(80),
  profileImageUrl: z.string().min(1),
  companyName: z.string().max(200).optional(),
  titleHint: z.string().max(200).optional(),
  persona: z.string().max(4000).optional(),
  responsibilities: hireAgentResponsibilitiesSchema,
  /** Person who receives conversation summaries and handoffs (may differ from signed-in employer). */
  leadTeamMember: hireContactSchema.optional(),
  /** Full location rows from website scrape — persisted to project on preview/deploy. */
  scrapedLocations: z.array(hireScrapedLocationSchema).optional(),
  /** Lead team member per scraped location name. */
  locationLeadMembers: z
    .array(
      z.object({
        locationName: z.string().min(1).max(200),
        leadTeamMember: hireContactSchema,
      }),
    )
    .optional(),
  /** Organization uses departments at one location (schools, nonprofits). */
  organizationLayout: z.enum(["locations", "departments"]).optional(),
  /** Lead and other team members per department. */
  departmentMembers: z
    .array(
      z.object({
        departmentName: z.string().min(1).max(200),
        leadTeamMember: hireContactSchema,
        otherTeamMemberEmails: z.array(z.string().email()).optional(),
      }),
    )
    .optional(),
  /** Additional team members collected during onboarding (e.g. Trish hire builder). */
  extraTeamMembers: z
    .array(
      z.object({
        name: z.string().min(1).max(200),
        email: z.string().email(),
        title: z.string().max(200).optional(),
        phone: z.string().max(40).optional(),
        imageUrl: z.string().max(2000).optional(),
        /** Department this person should lead / oversee (schools, nonprofits). */
        departmentName: z.string().max(200).optional(),
        /** Location this person should lead (multi-location businesses). */
        locationName: z.string().max(200).optional(),
      }),
    )
    .optional(),
  /**
   * Scheduling / appointments integration selected during hire
   * (e.g. google_calendar). Used to set Active Appointments Tool after OAuth.
   */
  schedulingIntegrationId: z.string().max(80).optional(),
  /** Signed-in employer / account owner from the hire form. */
  employerContact: hireContactSchema.optional(),
  widgetColors: z
    .object({
      headerColor: z.string().optional(),
      widgetButtonColor: z.string().optional(),
      launcherBackgroundColor: z.string().optional(),
      textColor: z.string().optional(),
    })
    .optional(),
  teamHandoffCtaClusters: z
    .array(
      z.object({
        ctaQuestion: z.string(),
        qualificationQuestions: z.array(z.string()),
        locationRouting: z.string().default("all"),
      }),
    )
    .optional(),
  /** Marketing twin that started the hire (SiteGlue → SiteGlue AI org). */
  marketingBrand: z.enum(["siteglue", "inbound_gpt"]).optional(),
  /** Partner onboarding brand slug (?brand=practicepromotions). */
  partnerBrandSlug: z.string().min(2).max(48).optional(),
  /**
   * Dashboard "+ Build new agent" (/onboard?new=1): prefer the user's active Clerk org
   * so Amplify360 (or SiteGlue AI) builds land in the org they have selected.
   * Public siteglue.ai CTAs omit this and stay on the SiteGlue AI portal org.
   */
  preferActiveOrganization: z.boolean().optional(),
});

export type HireAgentPreferences = z.infer<typeof hireAgentPreferencesSchema>;

export function resolveHireOrgResolution(
  prefs: HireAgentPreferences | null | undefined,
  marketingBrandOverride?: "siteglue" | "inbound_gpt",
): {
  marketingBrand: "siteglue" | "inbound_gpt";
  partnerBrandSlug: string | null;
} {
  const partnerBrandSlug = prefs?.partnerBrandSlug?.trim() || null;
  const marketingBrand =
    marketingBrandOverride ??
    (prefs?.marketingBrand === "siteglue" ? "siteglue" : "inbound_gpt");
  return { marketingBrand, partnerBrandSlug };
}

export const DEFAULT_HIRE_RESPONSIBILITIES: HireAgentResponsibilities = {
  liveChat: true,
  teamHandoff: true,
  textMessages: false,
  email: false,
  browserCalls: false,
  phoneCalls: false,
  videoCalls: false,
  welcomeVideo: false,
  messenger: false,
  whatsapp: false,
  appointmentsScheduling: false,
};

export const hireAgentPreferencesSessionKey = (projectId: string) =>
  `egpt-hire-agent-prefs:${projectId}`;

export function buildChannelsPartialFromHirePreferences(
  r: HireAgentResponsibilities,
): {
  aiChat: boolean;
  call: boolean;
  talk: boolean;
  sms: boolean;
  email: boolean;
  messenger: boolean;
  whatsapp: boolean;
  chatTeamHandoffDelegateEnabled: boolean;
  chatAppointmentsEnabled: boolean;
} {
  return {
    aiChat: r.liveChat,
    call: r.phoneCalls,
    talk: r.browserCalls || r.videoCalls,
    sms: r.textMessages,
    email: r.email,
    messenger: r.messenger,
    whatsapp: r.whatsapp,
    chatTeamHandoffDelegateEnabled: r.teamHandoff,
    chatAppointmentsEnabled: r.appointmentsScheduling,
  };
}

export function readHireAgentPreferences(
  projectId: string,
): HireAgentPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(hireAgentPreferencesSessionKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw) as HireAgentPreferences;
  } catch {
    return null;
  }
}

export function writeHireAgentPreferences(
  projectId: string,
  prefs: HireAgentPreferences,
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      hireAgentPreferencesSessionKey(projectId),
      JSON.stringify(prefs),
    );
  } catch {
    /* ignore */
  }
}

export function hireContactToLeadForm(contact: HireContact): {
  name: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
} {
  return {
    name: `${contact.firstName} ${contact.lastName}`.trim(),
    firstName: contact.firstName,
    lastName: contact.lastName,
    jobTitle: "Lead Team Member",
    email: contact.email,
    phone: contact.phone?.trim() ?? "",
  };
}

export function clearHireAgentPreferences(projectId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(hireAgentPreferencesSessionKey(projectId));
  } catch {
    /* ignore */
  }
}

const hireEmailSchema = z.string().email();

/** Ensure contact fields satisfy hireContactSchema (e.g. single-word names → placeholder last name). */
export function normalizeHireContact(
  contact: Partial<HireContact> & { email: string },
): HireContact | null {
  const email = contact.email?.trim();
  if (!email || !hireEmailSchema.safeParse(email).success) {
    return null;
  }

  let firstName = contact.firstName?.trim() ?? "";
  let lastName = contact.lastName?.trim() ?? "";

  if (!firstName && !lastName) {
    return null;
  }

  if (!firstName) {
    firstName = lastName.split(/\s+/)[0] ?? "Team";
  }
  if (!lastName) {
    lastName = ".";
  }

  const phone = contact.phone?.trim();

  return {
    firstName: firstName.slice(0, 80),
    lastName: lastName.slice(0, 80),
    email,
    ...(phone ? { phone: phone.slice(0, 40) } : {}),
  };
}

export type SerializeHirePreferencesResult =
  | { ok: true; data: HireAgentPreferences }
  | { ok: false; message: string };

/** Normalize hire preferences before tRPC mutations (prevents generic "Bad Request" Zod failures). */
export function serializeHirePreferencesForApi(
  prefs: HireAgentPreferences,
  options?: { defaultProfileImageUrl?: string },
): SerializeHirePreferencesResult {
  const profileImageUrl =
    prefs.profileImageUrl?.trim() || options?.defaultProfileImageUrl?.trim() || "";
  if (!profileImageUrl) {
    return { ok: false, message: "Choose an agent profile photo before hiring." };
  }

  const agentName = prefs.agentName?.trim();
  if (!agentName) {
    return { ok: false, message: "Enter your agent name before hiring." };
  }

  let persona = prefs.persona?.trim();
  if (persona && persona.length > 4000) {
    persona = persona.slice(0, 4000);
  }

  const serialized: HireAgentPreferences = {
    agentName: agentName.slice(0, 80),
    profileImageUrl,
    responsibilities: prefs.responsibilities,
    ...(prefs.companyName?.trim()
      ? { companyName: prefs.companyName.trim().slice(0, 200) }
      : {}),
    ...(prefs.titleHint?.trim() ? { titleHint: prefs.titleHint.trim().slice(0, 200) } : {}),
    ...(persona ? { persona } : {}),
    ...(prefs.widgetColors ? { widgetColors: prefs.widgetColors } : {}),
  };

  if (prefs.leadTeamMember) {
    const lead = normalizeHireContact(prefs.leadTeamMember);
    if (lead) {
      serialized.leadTeamMember = lead;
    }
  }

  if (prefs.employerContact) {
    const employer = normalizeHireContact(prefs.employerContact);
    if (employer) {
      serialized.employerContact = employer;
    }
  }

  if (prefs.scrapedLocations?.length) {
    const scrapedLocations = prefs.scrapedLocations
      .map((loc) => ({
        name: loc.name.trim().slice(0, 200),
        ...(loc.address?.trim() ? { address: loc.address.trim().slice(0, 500) } : {}),
        ...(loc.city?.trim() ? { city: loc.city.trim().slice(0, 120) } : {}),
        ...(loc.state?.trim() ? { state: loc.state.trim().slice(0, 20) } : {}),
        ...(loc.postalCode?.trim() ? { postalCode: loc.postalCode.trim().slice(0, 20) } : {}),
        ...(loc.phone?.trim() ? { phone: loc.phone.trim().slice(0, 40) } : {}),
        ...(loc.businessHours?.trim()
          ? { businessHours: loc.businessHours.trim().slice(0, 500) }
          : {}),
      }))
      .filter((loc) => loc.name.length > 0);
    if (scrapedLocations.length > 0) {
      serialized.scrapedLocations = scrapedLocations;
    }
  }

  if (prefs.locationLeadMembers?.length) {
    const locationLeadMembers = prefs.locationLeadMembers
      .map((entry) => {
        const lead = normalizeHireContact(entry.leadTeamMember);
        if (!lead) return null;
        const locationName = entry.locationName.trim().slice(0, 200);
        if (!locationName) return null;
        return { locationName, leadTeamMember: lead };
      })
      .filter(
        (entry): entry is { locationName: string; leadTeamMember: HireContact } =>
          entry !== null,
      );
    if (locationLeadMembers.length > 0) {
      serialized.locationLeadMembers = locationLeadMembers;
    }
  }

  if (prefs.organizationLayout === "departments" || prefs.organizationLayout === "locations") {
    serialized.organizationLayout = prefs.organizationLayout;
  }

  if (prefs.departmentMembers?.length) {
    const departmentMembers = prefs.departmentMembers
      .map((entry) => {
        const lead = normalizeHireContact(entry.leadTeamMember);
        if (!lead) return null;
        const departmentName = entry.departmentName.trim().slice(0, 200);
        if (!departmentName) return null;
        const otherTeamMemberEmails = (entry.otherTeamMemberEmails ?? [])
          .map((email) => email.trim().toLowerCase())
          .filter((email) => hireEmailSchema.safeParse(email).success);
        return {
          departmentName,
          leadTeamMember: lead,
          ...(otherTeamMemberEmails.length > 0 ? { otherTeamMemberEmails } : {}),
        };
      })
      .filter(
        (
          entry,
        ): entry is {
          departmentName: string;
          leadTeamMember: HireContact;
          otherTeamMemberEmails?: string[];
        } => entry !== null,
      );
    if (departmentMembers.length > 0) {
      serialized.departmentMembers = departmentMembers;
    }
  }

  if (prefs.extraTeamMembers?.length) {
    const extraTeamMembers = prefs.extraTeamMembers
      .map((member) => {
        const email = member.email?.trim().toLowerCase() ?? "";
        if (!hireEmailSchema.safeParse(email).success) return null;
        const name = member.name?.trim().slice(0, 200);
        if (!name) return null;
        return {
          name,
          email,
          ...(member.title?.trim() ? { title: member.title.trim().slice(0, 200) } : {}),
          ...(member.phone?.trim() ? { phone: member.phone.trim().slice(0, 40) } : {}),
          ...(member.imageUrl?.trim()
            ? { imageUrl: member.imageUrl.trim().slice(0, 2000) }
            : {}),
          ...(member.departmentName?.trim()
            ? { departmentName: member.departmentName.trim().slice(0, 200) }
            : {}),
          ...(member.locationName?.trim()
            ? { locationName: member.locationName.trim().slice(0, 200) }
            : {}),
        };
      })
      .filter(
        (
          member,
        ): member is NonNullable<HireAgentPreferences["extraTeamMembers"]>[number] =>
          member !== null,
      );
    if (extraTeamMembers.length > 0) {
      serialized.extraTeamMembers = extraTeamMembers;
    }
  }

  if (prefs.schedulingIntegrationId?.trim()) {
    serialized.schedulingIntegrationId = prefs.schedulingIntegrationId.trim().slice(0, 80);
  }

  if (prefs.teamHandoffCtaClusters?.length) {
    const clusters = normalizeHireTeamHandoffCtaClusters(prefs.teamHandoffCtaClusters, []);
    if (clusters.length > 0) {
      serialized.teamHandoffCtaClusters = clusters;
    }
  }

  const parsed = hireAgentPreferencesSchema.safeParse(serialized);
  if (!parsed.success) {
    return {
      ok: false,
      message: "Some agent settings are invalid. Refresh the page and try again.",
    };
  }

  return { ok: true, data: parsed.data };
}
